import { media, Stack } from '@koob/margaret';
import styled from 'styled-components';
import React, { useState } from 'react';
import ExperienceTypeIcon from '../Partials/ExperienceTypeIcon';
import { useTranslation } from 'react-i18next';
import TypologyButton from '../Partials/TypologyButton';
import { EXPERIENCE_TYPOLOGY_SCHEMA } from '../Utilities/ExperienceValidationSchemas';
import { useField } from 'formik';
import { KoobButton, KoobInputSelect } from '@koob/ui';
import { useApp, useError, useSnack } from '../../../../hooks';
import { post } from 'api/node';
import { duplicateExperience } from 'api/node/experiences';
import { useNavigate } from 'react-router-dom';
import KoobLoadingOverlay from '@koob/ui/src/components/KoobLoadingOverlay';
import { useAsync } from 'react-async';
import { useDebounce } from 'use-debounce';
import { decodeId } from 'utils';

const getList = async ({ search = '' }) => {
  return await post('/experiences/liste', {
    search,
  });
};

const Separator = styled.div`
  width: 1px;
  height: 60%;
  background-color: ${({ theme }) => theme.separatorLight};

  ${media.desktop`
    width: 60%;
    height: 2px;
  `};
`;

export const TypologyItem = ({
  selected,
  onSelect,
  type,
  name,
  description,
}) => {
  return (
    <>
      <div className="w-48 flex flex-col items-center">
        <TypologyButton
          selected={selected}
          onSelect={() => onSelect(type)}
          icon={<ExperienceTypeIcon type={type} />}
          name={name}
        />

        <div className="mt-3 text-sm text-center text-gray-500">
          {description}
        </div>
      </div>
    </>
  );
};

export const ExperienceTypology = ({ onSelectExperience }) => {
  const { t } = useTranslation('experiences');
  const [{ value }, , { setValue }] = useField('type');
  const { sendErrorSnack } = useError();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { currentOrganizationId } = useApp();

  const { notify } = useSnack();

  const handleImport = async (experienceId) => {
    try {
      setIsLoading(true);
      const data = await duplicateExperience(experienceId);
      onSelectExperience(data?.data);
      notify(t('importSuccess'));
      if (data?.data?.id) {
        navigate(`/experiences/${data.data.id}`);
      }
    } catch (error) {
      sendErrorSnack(error);
    } finally {
      setIsLoading(false);
    }
  };
  const [experienceId, setExperienceId] = useState(null);
  const [experienceSearch, setExperienceSearch] = useState('');
  const [debouncedExperienceSearch] = useDebounce(experienceSearch, 500);

  const { data: experiencesData } = useAsync({
    promiseFn: getList,
    search: debouncedExperienceSearch,
    watch: debouncedExperienceSearch,
  });

  const experienceOptions = (experiencesData?.data ?? [])
    .filter(
      (item) =>
        item?.organizationId?.toString() ===
        decodeId(currentOrganizationId)?.toString(),
    )
    .map((item) => ({ value: item.id, label: item.name }));

  return (
    <>
      {isLoading && (
        <KoobLoadingOverlay
          opacity="semi"
          title={t('duplicating.title')}
          description={t('duplicating.description')}
        />
      )}
      <Stack gutterSize={1.5} alignY="center" padding={6} size="full">
        <div className="w-full flex items-start justify-around">
          <TypologyItem
            selected={value === 'Transfer'}
            onSelect={() => setValue('Transfer')}
            type="Transfer"
            name={t('types.transfer')}
            description={t('typology.transferDescription')}
          />

          <TypologyItem
            selected={value === 'Program'}
            onSelect={() => setValue('Program')}
            type="Program"
            name={t('types.program')}
            description={t('typology.programDescription')}
          />

          <TypologyItem
            selected={value === 'Activity'}
            onSelect={() => setValue('Activity')}
            type="Activity"
            name={t('types.extraAndActivity')}
            description={t('typology.extraAndActivityDescription')}
          />
        </div>
        <Stack alignX="center" padding={1}>
          <Separator />
        </Stack>
      </Stack>

      <div className="max-w-2xl mx-auto flex justify-center items-end space-x-3">
        <div className="w-96">
          <div className="mb-2 font-medium text-gray-600">
            {t(`startFromExisting`)}
          </div>

          <KoobInputSelect
            options={experienceOptions}
            value={experienceId}
            onValueChange={setExperienceId}
            searchable
            onSearchChange={setExperienceSearch}
            clearable
          />
        </div>

        <KoobButton
          disabled={!experienceId}
          variant="primary"
          onClick={() => {
            handleImport(experienceId);
          }}
        >
          {t('misc:import')}
        </KoobButton>
      </div>
    </>
  );
};

export const getSchema = () => EXPERIENCE_TYPOLOGY_SCHEMA;
